/** @format */
import { HttpErrorResponse } from '@angular/common/http';
import { DOCUMENT, ErrorHandler, Inject, Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ExpectedPermissionsResponse, LocalizedString } from 'common-typescript/types';
import { omit } from 'lodash-es';
import { EMPTY, Observable, of, OperatorFunction, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { AuthService, DowngradedService, ServiceDowngradeMappings, StaticMembers } from 'sis-common';
import { AlertsService, AlertType } from '../alerts/alerts-ng.service';
import { CommonCodeService, IndexedCodes } from '../service/common-code.service';
import { accessDeniedModalFdOpener } from './access-denied-modal/access-denied-modal-fd.component';
import { accessDeniedModalOpener } from './access-denied-modal/access-denied-modal.component';
import { LogService } from './log.service';
import { systemErrorModalFdOpener, SystemErrorModalFdValues } from './system-error-modal/system-error-modal-fd.component';
import { systemErrorModalOpener, SystemErrorModalValues } from './system-error-modal/system-error-modal.component';
import { isAccessDeniedResponse, isConstraintViolationExplanation, isSisuLocalizedException } from './type-guards';
import { ValidationErrorHandlerService } from './validation-error-handler.service';
import { validationErrorModalFdOpener } from './validation-error-modal/validation-error-modal-fd.component';
import { validationErrorModalOpener, ValidationErrorModalValues } from './validation-error-modal/validation-error-modal.component';

@Injectable({ providedIn: 'root' })
@StaticMembers<DowngradedService>()
export class AppErrorHandler implements ErrorHandler, OnDestroy {
    static downgrade: ServiceDowngradeMappings = {
        moduleName: 'sis-components.error-handler.appErrorHandler.downgraded',
        serviceName: 'appErrorHandler',
    };

    private readonly openAccessDeniedModal = accessDeniedModalOpener();
    private readonly openSystemErrorModal = systemErrorModalOpener();
    private readonly openValidationErrorModal = validationErrorModalOpener();
    private readonly openAccessDeniedModalFd = accessDeniedModalFdOpener();
    private readonly openSystemErrorModalFd = systemErrorModalFdOpener();
    private readonly openValidationErrorModalFd = validationErrorModalFdOpener();
    private readonly destroy$ = new Subject<void>();

    private isSystemErrorModalOpen = false;
    private isSystemErrorModalFdOpen = false;

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly logService: LogService,
        private readonly alertService: AlertsService,
        private readonly codeService: CommonCodeService,
        private readonly translate: TranslateService,
        private readonly validationErrorHandler: ValidationErrorHandlerService,
        private readonly authService: AuthService,
    ) {
        this.logService = logService;
        this.alertService = alertService;
        this.codeService = codeService;
        this.document = document;
        this.translate = translate;
        this.validationErrorHandler = validationErrorHandler;
        this.registerErrorResponseEvents();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    registerErrorResponseEvents(): void {
        this.authService.errorResponse$.pipe(takeUntil(this.destroy$)).subscribe((error: HttpErrorResponse) => {
            this.handleErrorResponse(error);
        });
    }

    /**
     * Global Angular error handler. All unhandled errors in the Angular part of the application will be caught here.
     * Logs all errors to the backend, and shows a system error modal with the error details to the user.
     *
     * NOTE: this is also called directly by {@link defaultErrorHandler} for non-HTTP errors caught in an observable
     * pipe — it always shows the legacy NgbModal-based modal, since the true global-handler usage has no way to know
     * whether a Fudis dialog is open. Callers using {@link defaultErrorHandlerFd} get {@link handleErrorFd} instead.
     */
    async handleError(error: any) {
        this.logService.critical('Angular', { message: 'COMMON.SYSTEM_ERROR', ...this.getCommonLogFields() }, error);
        this.openSystemErrorModalUnlessAlreadyOpen(this.buildUnhandledErrorValues(error));
    }

    // Fudis-dialog-safe counterpart of handleError, for use by defaultErrorHandlerFd's non-HTTP-error branch only —
    // not registered as the app's global ErrorHandler, since that path has no dialog context to be Fudis-safe about.
    private handleErrorFd(error: any): void {
        this.logService.critical('Angular', { message: 'COMMON.SYSTEM_ERROR', ...this.getCommonLogFields() }, error);
        this.openSystemErrorModalFdUnlessAlreadyOpen(this.buildUnhandledErrorValues(error));
    }

    private buildUnhandledErrorValues(error: any): SystemErrorModalValues {
        let stacktrace: string;
        let errorDetails: string;
        if (error instanceof Error) {
            stacktrace = error.stack;
            errorDetails = error.message;
        } else if (typeof error === 'string') {
            errorDetails = error;
        }

        return {
            errorDetails,
            stacktrace,
            title: this.translate.instant('COMMON.SYSTEM_ERROR'),
            userAgent: this.document.defaultView.navigator.userAgent,
        };
    }

    /**
     * Catches all errors in the observable and handles them. If an error occurs, the observable will close without emitting anything.
     *
     * Different types of errors are handled differently:
     *
     * - HTTP 401, 404 and 419 errors are ignored
     * - HTTP 403 results in either an alert or an access denied modal
     * - HTTP 422 results in a validation error modal
     * - HTTP 409 and 423 result in a system error modal
     * - All other HTTP errors are logged to the backend and result in a system error modal
     * - All JS/TS errors are logged to the backend and result in a system error modal
     *
     * @see {@link defaultErrorHandlerFd} — use that instead when the calling component is itself hosted in a Fudis
     * dialog (opened via `FudisDialogService`), so error modals stack correctly on top of it instead of behind it.
     */
    defaultErrorHandler<T>(): OperatorFunction<T, T>;

    /**
     * A variation of {@link defaultErrorHandler} that emits a value after an error has occurred. The observable will close after
     * emitting the value.
     *
     * @param emitOnError The value to emit after an error occurred. `null` is acceptable, `undefined` is not. Useful e.g. when
     * needing to update some component state to a sensible value as a result of an error, to prevent broken/stale UI state.
     */
    defaultErrorHandler<T, R>(emitOnError?: R): OperatorFunction<T, R>;

    defaultErrorHandler<T, R>(emitOnError?: R): OperatorFunction<T, T | R> {
        return catchError<T, Observable<R | never>>((err: unknown) => {
            const result = emitOnError !== undefined ? of(emitOnError) : EMPTY;
            if (!err) {
                return result;
            }

            if (err instanceof HttpErrorResponse) {
                this.handleErrorResponse(err);
            } else {
                this.handleError(err);
            }

            return result;
        });
    }

    /**
     * A variant of {@link defaultErrorHandler} for use from within a component that is itself hosted in a Fudis dialog
     * (opened via `FudisDialogService`). Shows system/access-denied/validation errors using Fudis-native dialogs instead
     * of the legacy NgbModal-based ones, so they render in the same CDK overlay stack as the hosting dialog instead of
     * behind it.
     */
    defaultErrorHandlerFd<T>(): OperatorFunction<T, T>;

    /**
     * A variation of {@link defaultErrorHandlerFd} that emits a value after an error has occurred. The observable will close
     * after emitting the value.
     *
     * @param emitOnError The value to emit after an error occurred. `null` is acceptable, `undefined` is not. Useful e.g. when
     * needing to update some component state to a sensible value as a result of an error, to prevent broken/stale UI state.
     */
    defaultErrorHandlerFd<T, R>(emitOnError?: R): OperatorFunction<T, R>;

    defaultErrorHandlerFd<T, R>(emitOnError?: R): OperatorFunction<T, T | R> {
        return catchError<T, Observable<R | never>>((err: unknown) => {
            const result = emitOnError !== undefined ? of(emitOnError) : EMPTY;
            if (!err) {
                return result;
            }

            if (err instanceof HttpErrorResponse) {
                this.handleErrorResponseFd(err);
            } else {
                this.handleErrorFd(err);
            }

            return result;
        });
    }

    private handleErrorResponse(response: HttpErrorResponse): void {
        this.handleErrorResponseWith(
            response,
            expectedPermissions => this.openAccessDeniedModal({ expectedPermissions }),
            values => this.openValidationErrorModal(values),
            values => this.openSystemErrorModalUnlessAlreadyOpen(values),
        );
    }

    // Fudis-dialog-safe counterpart of handleErrorResponse: same routing, but system/access-denied/validation errors
    // open via FudisDialogService instead of NgbModal.
    private handleErrorResponseFd(response: HttpErrorResponse): void {
        this.handleErrorResponseWith(
            response,
            expectedPermissions => this.openAccessDeniedModalFd({ expectedPermissions }),
            values => this.openValidationErrorModalFd(values),
            values => this.openSystemErrorModalFdUnlessAlreadyOpen(values),
        );
    }

    private handleErrorResponseWith(
        response: HttpErrorResponse,
        openAccessDeniedModal: (expectedPermissions: LocalizedString[]) => void,
        openValidationErrorModal: (values: ValidationErrorModalValues) => void,
        openSystemErrorModal: (values: SystemErrorModalValues) => void,
    ): void {
        const { error } = response;
        switch (response.status) {
            case -1: // Canceled or timed out
            case 0: // Unknown error
            case 401: // Unauthorized
            case 404: // Not Found
            case 419: // Authentication Timeout
                // Ignore errors with any of the above statuses
                return;

            case 403: // Forbidden
                this.handleForbiddenWith(error, openAccessDeniedModal);
                break;

            case 422: // Unprocessable Content (i.e. validation error)
                openValidationErrorModal(this.buildValidationErrorValues(error));
                break;

            case 409: // Conflict
            case 423: // Locked
                openSystemErrorModal(
                    this.buildSystemErrorValues(
                        response,
                        `ERROR.BACKEND.${response.status}.TITLE`,
                        `ERROR.BACKEND.${response.status}.MESSAGE`,
                    ),
                );
                break;

            default:
                this.logErrorResponse(response);
                openSystemErrorModal(
                    this.buildSystemErrorValues(response, 'ERROR.BACKEND.DEFAULT.TITLE', `ERROR.BACKEND.${response.status}.MESSAGE`),
                );
        }
    }

    private handleForbidden(error: unknown): void {
        this.handleForbiddenWith(error, expectedPermissions => this.openAccessDeniedModal({ expectedPermissions }));
    }

    // Fudis-dialog-safe counterpart of handleForbidden: same routing, but opens the access-denied dialog via
    // FudisDialogService instead of NgbModal.
    private handleForbiddenFd(error: unknown): void {
        this.handleForbiddenWith(error, expectedPermissions => this.openAccessDeniedModalFd({ expectedPermissions }));
    }

    private handleForbiddenWith(error: unknown, openAccessDeniedModal: (expectedPermissions: LocalizedString[]) => void): void {
        if (isAccessDeniedResponse(error)) {
            switch (error.accessDeniedType) {
                case 'USER_NOT_FOUND':
                case 'MISSING_ROLE':
                case 'NO_LOGIN_TIME_WINDOW':
                case 'USER_LOGIN_DISABLED':
                    // /ori/preauth-only types — handled by app-specific routing (see NoSisuAccountRedirectService).
                    break;
                case 'EXPECTED_PERMISSIONS':
                    this.handleExpectedPermissionsResponse(error as ExpectedPermissionsResponse, openAccessDeniedModal);
                    break;
                case 'DEFAULT':
                default:
                    this.showDefaultAccessDeniedAlert();
            }
        } else {
            this.showDefaultAccessDeniedAlert();
        }
    }

    private handleExpectedPermissionsResponse(
        error: ExpectedPermissionsResponse,
        openAccessDeniedModal: (expectedPermissions: LocalizedString[]) => void,
    ): void {
        const { expectedPermissions: expectedPermissionUrns } = error;
        if (expectedPermissionUrns?.length > 0) {
            const fallbackName = (permissionUrn: string) => ({ fi: permissionUrn, en: permissionUrn, sv: permissionUrn });
            this.codeService
                .getCodebookObservable('urn:code:permission')
                .pipe(
                    catchError(() => of({} as IndexedCodes)),
                    map(codes => expectedPermissionUrns.map(({ urn }) => codes[urn]?.name ?? fallbackName(urn))),
                    take(1),
                )
                .subscribe(openAccessDeniedModal);
        } else {
            this.showDefaultAccessDeniedAlert();
        }
    }

    /**
     * Builds the display values for a 422 validation error response, without opening any modal/dialog.
     *
     * NOTE: The translation keys here are not scoped, so they won't work with Transloco.
     */
    private buildValidationErrorValues(error: unknown): ValidationErrorModalValues {
        if (isSisuLocalizedException(error)) {
            return {
                titleKey: error.titleKey,
                messageKey: error.messageKey,
            };
        }
        return {
            titleKey: 'ERROR.BACKEND.CONSTRAINT_VIOLATION_TITLE',
            validationErrors: isConstraintViolationExplanation(error) ? this.validationErrorHandler.parseViolations(error) : null,
        };
    }

    // Return type is the shared shape of SystemErrorModalValues and SystemErrorModalFdValues, so callers can pass the
    // result to either opener.
    private buildSystemErrorValues(
        response: HttpErrorResponse,
        defaultTitleKey: string,
        defaultMessageKey: string,
    ): SystemErrorModalValues {
        let title;
        let message;
        const { error } = response;
        if (isSisuLocalizedException(error)) {
            title = this.translate.instant(error.titleKey ?? defaultTitleKey);
            message = error.messageKey
                ? this.translateIfExists(error.messageKey, { value: error.messageValue })
                : this.translateIfExists(defaultMessageKey);
        } else {
            title = this.translate.instant(defaultTitleKey);
            message = this.translateIfExists(defaultMessageKey) ?? error?.message ?? response.statusText;
        }

        return {
            title,
            message,
            errorCode: response.status,
            errorDetails: typeof error === 'object' ? JSON.stringify(error) : error.toString(),
            userAgent: this.document.defaultView.navigator.userAgent,
            stacktrace: `URL: ${response.url}`,
        };
    }

    private getCommonLogFields() {
        return {
            href: this.document.defaultView.location.href,
            userAgent: this.document.defaultView.navigator.userAgent,
        };
    }

    private logErrorResponse(response: HttpErrorResponse): void {
        const logObject = {
            ...response,
            ...this.getCommonLogFields(),
        };
        // The headers can contain authorization tokens, don't log them
        this.logService.error('ResponseError', omit(logObject, 'headers'));
    }

    private openSystemErrorModalUnlessAlreadyOpen(values: SystemErrorModalValues): void {
        this.openModalUnlessAlreadyOpen(
            () => this.isSystemErrorModalOpen,
            isOpen => (this.isSystemErrorModalOpen = isOpen),
            () => this.openSystemErrorModal(values).hidden,
        );
    }

    private openSystemErrorModalFdUnlessAlreadyOpen(values: SystemErrorModalFdValues): void {
        this.openModalUnlessAlreadyOpen(
            () => this.isSystemErrorModalFdOpen,
            isOpen => (this.isSystemErrorModalFdOpen = isOpen),
            () => this.openSystemErrorModalFd(values).afterClosed(),
        );
    }

    private openModalUnlessAlreadyOpen(isOpen: () => boolean, setOpen: (isOpen: boolean) => void, open: () => Observable<unknown>): void {
        if (!isOpen()) {
            setOpen(true);
            open().subscribe(() => setOpen(false));
        }
    }

    private showDefaultAccessDeniedAlert(): void {
        this.alertService.addTemporaryAlert({
            type: AlertType.DANGER,
            message: this.translate.instant('SIS_COMPONENTS.ACCESS_DENIED.ALERT_DEFAULT'),
        });
    }

    private translateIfExists(key: string, params?: Object): string | null {
        const translation = !!key ? this.translate.instant(key, params) : key;
        return translation === key ? null : translation;
    }
}
